import {
  AdaptedGetShareQuotesApiResponse,
  AdaptedStockDTO,
  GetShareQuotesApiResponse,
  LineChartData,
  MarketInfoFormattedFields,
  MarketInfoResponse,
  PortfolioInfoApiResponse,
  PortfolioInfoFormattedFields,
  PortfolioPositionApiResponse,
  PortfolioPositionFormattedFields,
  StockDetailsTimespan,
  StockDTO,
  StockPriceDTO,
} from './sharesTypes'

import { currencyUtils } from '@/utils'

const { formatFiatCurrency } = currencyUtils

const formatDateByLocale = (
  date: Date,
  options?: Intl.DateTimeFormatOptions,
): string => {
  return new Intl.DateTimeFormat(undefined, options).format(date)
}

const convertTimestampInSecondsToDate = (timestamp: number): Date => {
  return new Date(timestamp * 1000)
}

const formatTimeByLocale = (dateString: string): string => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const adapterStockPrices = (
  stockPrices: StockPriceDTO[],
  period: StockDetailsTimespan,
): LineChartData[] =>
  stockPrices.map((item) => {
    const dateToFormat = convertTimestampInSecondsToDate(item.timestamp)

    const dateByLocale = formatDateByLocale(dateToFormat)

    const options: Intl.DateTimeFormatOptions =
      period === 'DAY'
        ? {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          }
        : { day: '2-digit', month: 'short', year: 'numeric' }

    const lineChartDateFormatted = formatDateByLocale(dateToFormat, options)

    return {
      priceUsd: Number(item.close),
      time: item.timestamp,
      date: dateByLocale,
      lineChartDateFormatted,
    }
  })

const getSign = (value: number | string = 0): string => {
  const num = Number(value)
  return num >= 0 ? '+' : '-'
}

const formatChangePercent = (
  value: number | string = 0,
  withoutSign = false,
): string => {
  const num = Number(value)
  const formatted = Math.abs(num).toFixed(2)
  const sign = withoutSign ? '' : getSign(num)

  return `${sign}${formatted}%`
}

export const adapterStock = (stock: StockDTO): AdaptedStockDTO => {
  const formatted_stock_price = formatFiatCurrency({
    value: stock?.stock_price ?? 0,
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_change_price = formatFiatCurrency({
    value: stock?.change_price ?? 0,
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_change_percent = formatChangePercent(
    stock?.change_percent ?? 0,
  )

  const formatted_holding_value = formatFiatCurrency({
    value: Number(stock?.holding_value ?? 0),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const adapted_shares = Number(stock?.shares_count ?? 0).toLocaleString(
    undefined,
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    },
  )

  const formatted_shares = `${adapted_shares} shares`

  const formatted_daily_change_price = formatFiatCurrency({
    value: Math.abs(Number(stock?.daily_change_price ?? 0)),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_daily_change_price_with_sign = `${getSign(Number(stock?.daily_change_price ?? 0))}${formatted_daily_change_price}`

  const formatted_daily_change_percent = formatChangePercent(
    stock?.daily_change_percent ?? 0,
  )

  return {
    ...stock,
    formatted_stock_price,
    formatted_change_price,
    formatted_change_percent,
    formatted_holding_value,
    formatted_shares,
    formatted_daily_change_price: formatted_daily_change_price_with_sign,
    formatted_daily_change_percent,
  }
}

const adapterShareQuotes = (
  shareQuotes: GetShareQuotesApiResponse,
): AdaptedGetShareQuotesApiResponse => {
  const formatted_fee = formatFiatCurrency({
    value: Number(shareQuotes.fee ?? 0),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_price_per_unit = formatFiatCurrency({
    value: Number(shareQuotes.price_per_unit ?? 0),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_total_cost = formatFiatCurrency({
    value: Number(shareQuotes.total_cost ?? 0),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const adapted_estimated_shares = Number(
    shareQuotes.estimated_shares ?? 0,
  ).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  })

  const formatted_estimated_shares = `\u2248 ${adapted_estimated_shares} shares`

  return {
    ...shareQuotes,
    formatted_fee,
    formatted_price_per_unit,
    formatted_total_cost,
    formatted_estimated_shares,
  }
}

const adapterPortfolioInfo = (
  portfolioInfo: PortfolioInfoApiResponse['portfolio_info'],
): PortfolioInfoApiResponse['portfolio_info'] & PortfolioInfoFormattedFields => {
  const formatted_portfolio_value = formatFiatCurrency({
    value: Number(portfolioInfo.portfolio_value ?? 0),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_total_return_usd_value = formatFiatCurrency({
    value: Math.abs(Number(portfolioInfo.total_return_usd ?? 0)),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_total_return_usd_with_sign = `${getSign(Number(portfolioInfo.total_return_usd ?? 0))}${formatted_total_return_usd_value}`

  const formatted_total_return_percent = formatChangePercent(
    portfolioInfo.total_return_percent ?? 0,
    true,
  )

  return {
    ...portfolioInfo,
    formatted_portfolio_value,
    formatted_total_return_usd: formatted_total_return_usd_with_sign,
    formatted_total_return_percent,
  }
}

const adapterMarketInfo = (
  marketInfo: MarketInfoResponse['market_info'],
): MarketInfoResponse['market_info'] & MarketInfoFormattedFields => {
  const formatted_next_session_open_time = formatTimeByLocale(
    marketInfo.next_session_open_dt ?? '',
  )
  const formatted_next_session_close_time = formatTimeByLocale(
    marketInfo.next_session_close_dt ?? '',
  )

  return {
    ...marketInfo,
    formatted_next_session_open_time,
    formatted_next_session_close_time,
  }
}

const adapterPortfolioPosition = (
  portfolioPosition: PortfolioPositionApiResponse['portfolio_position'],
): PortfolioPositionApiResponse['portfolio_position'] &
  PortfolioPositionFormattedFields => {
  const formatted_quantity = Number(
    portfolioPosition.quantity ?? 0,
  ).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  })

  const formatted_holding_value = formatFiatCurrency({
    value: Number(portfolioPosition.holding_value ?? 0),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_average_cost = formatFiatCurrency({
    value: Number(portfolioPosition.average_cost ?? 0),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_total_invested = formatFiatCurrency({
    value: Number(portfolioPosition.total_invested ?? 0),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_total_return_usd_value = formatFiatCurrency({
    value: Math.abs(Number(portfolioPosition.total_return_usd ?? 0)),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const formatted_total_return_usd_with_sign = `${getSign(Number(portfolioPosition.total_return_usd ?? 0))}${formatted_total_return_usd_value}`

  const formatted_total_return_percent = formatChangePercent(
    portfolioPosition.total_return_percent ?? 0,
    true,
  )

  return {
    ...portfolioPosition,
    formatted_quantity,
    formatted_holding_value,
    formatted_average_cost,
    formatted_total_invested,
    formatted_total_return_usd: formatted_total_return_usd_with_sign,
    formatted_total_return_percent,
  }
}

export const sharesAdapter = {
  adapterStock,
  adapterStockPrices,
  adapterShareQuotes,
  adapterPortfolioInfo,
  adapterPortfolioPosition,
  adapterMarketInfo,
}
